import { signMessage } from '@wagmi/core'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { STAKING_BOT } from '@/constants/blockchain'
import { useAuth } from '@/plugins/auth'
import { getWagmiConfig, l1NativeWagmiConfig } from '@/plugins/auth/config'
import { CustomError } from '@/utils/helpers'
import { convertTimestampDateAndTimeUTC, post } from '@/utils/utils'

import { useChainValues } from './useChain'

type StatusResponse = {
  valid: boolean
  can_claim: boolean
  last_claim?: string
  next_claim?: string
  error?: string
}

type CollectResponse = {
  valid: boolean
  error?: string
}

const useStakingBot = () => {
  // Hooks
  const { address } = useAuth()
  const { blockNumber } = useChainValues(l1NativeWagmiConfig.id)

  // State
  const [canCollect, setCanCollect] = useState<boolean>(false)
  const [lastCollectTimestamp, setLastCollectTimestamp] = useState<string>()
  const [nextCollectTimestamp, setNextCollectTimestamp] = useState<string>()
  const [error, setError] = useState<CustomError>({
    call: 'stakingBot',
    msg: '',
    isError: false,
  })

  // Fetch status
  const fetchStatus = useCallback(async () => {
    if (!address) return
    try {
      const retval = (await post(
        `${STAKING_BOT}/status`,
        `{ "address": "${address}" }`
      )) as StatusResponse
      // Set error if response not valid
      if (!retval.valid) {
        setError({
          call: 'stakingBot.status',
          msg: retval.error || 'Error connecting to Staking Bot',
          isError: true,
        })
      } else {
        // Set state
        if (retval.can_claim) {
          setCanCollect(true)
        } else {
          setCanCollect(false)
        }
        if (retval.last_claim) {
          setLastCollectTimestamp(retval.last_claim)
        }
        if (retval.next_claim) {
          setNextCollectTimestamp(retval.next_claim)
        }
      }
    } catch (err: any) {
      setError({ call: 'stakingBot.status', msg: err.message, isError: true })
    }
  }, [address])
  // Update on new blocks
  useEffect(() => {
    if (address) fetchStatus()
  }, [address, blockNumber, fetchStatus])

  // Build message to sign
  const timestamp = useMemo(() => convertTimestampDateAndTimeUTC(new Date()), [])
  const message = `Collect all my rewards on ${timestamp}`

  // Claim / Collect request
  const collectRewards = useCallback(async () => {
    if (!address) return
    // Get signature first
    let signature: string | undefined
    try {
      signature = await signMessage(getWagmiConfig(), { message })
    } catch (err: any) {
      setError({ call: 'stakingBot.collect', msg: 'Failed to get signature', isError: true })
      return
    }
    if (!signature) {
      setError({ call: 'stakingBot.collect', msg: 'Failed to get signature', isError: true })
      return
    }
    // Make request
    try {
      const retval = (await post(
        `${STAKING_BOT}/claim`,
        `{ "address": "${address}", "timestamp": "${timestamp}", "signature": "${signature}" }`
      )) as CollectResponse
      // Set error if response not valid
      if (!retval.valid) {
        setError({
          call: 'stakingBot.collect',
          msg: retval.error || 'Error connecting to Staking Bot',
          isError: true,
        })
      }
    } catch (err: any) {
      setError({ call: 'stakingBot.collect', msg: err.message, isError: true })
    }
  }, [address, timestamp])

  return {
    canCollect,
    lastCollectTimestamp,
    nextCollectTimestamp,
    collectRewards,
    error,
  }
}

export default useStakingBot
